/**
 * OpsFleetSafetyDrilldown — per CEO directive (Feb 2026):
 *
 *   State → Region → City → Agent (role + position) → Fleet-safety detail
 *
 * Unlike the generic OpsRegionalDrilldown (which pivots on Property),
 * fleet safety pivots on the AGENT who filed the report because a single
 * agent's report chain (pre-shift checklist → incident → photo evidence)
 * is what Safety & Risk Management needs to triage.
 *
 * The right-hand detail panel merges two data sources for the selected
 * agent:
 *   • vehicle_safety_reports (pre-/post-shift checklists)
 *   • safety_incidents        (accidents / vandalism / roadside, with
 *                              police case # + photo gallery)
 *
 * From the panel the officer can either update incident status inline
 * or fire the "Route to Safety & Risk Management" action, which posts
 * a task to the safety department inbox.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  MapPin, Shield, User, Camera, Phone, RefreshCw, ArrowRightLeft,
  ClipboardCheck, AlertTriangle, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES, normalizeState } from '../../lib/usStates';

const API = process.env.REACT_APP_BACKEND_URL;

const normState = (v) => normalizeState(v) || 'TX';

const TYPE_TONE = {
  collision: 'bg-red-700',
  minor_accident: 'bg-red-600',
  vandalism: 'bg-purple-700',
  theft: 'bg-purple-800',
  roadside_towing: 'bg-amber-700',
  roadside_battery: 'bg-amber-600',
  roadside_lockout: 'bg-amber-600',
  roadside_flat_tire: 'bg-amber-600',
  roadside_other: 'bg-amber-500',
  other: 'bg-zinc-700',
};

export default function OpsFleetSafetyDrilldown({ regions = [] }) {
  const [incidents, setIncidents] = useState([]);
  const [safetyReports, setSafetyReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  // Big City tab list — default Dallas so ops always lands on the
  // launch metro. Matches OpsRegionalDrilldown & Routes & Regions.
  const [selectedBigCity, setSelectedBigCity] = useState('Dallas');
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [routing, setRouting] = useState(false);

  // ── Data load ────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, sRes] = await Promise.all([
        fetch(`${API}/api/safety-incidents`, { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : [])),
        fetch(`${API}/api/vehicle-safety`, { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : [])),
      ]);
      setIncidents(Array.isArray(iRes) ? iRes : []);
      setSafetyReports(Array.isArray(sRes) ? sRes : []);
    } catch (e) {
      toast.error(`Fleet safety load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Available states + region filtering ─────────────────────────────
  // Full US catalog per CEO directive — every state selectable.
  const availableStates = US_STATES;
  const seededStateSet = useMemo(() => {
    const s = new Set();
    (regions || []).forEach((r) => { if (r.state) s.add(normState(r.state)); });
    return s;
  }, [regions]);

  useEffect(() => {
    if (selectedState !== null) return;
    if (availableStates.length === 0) return;
    const firstSeeded = availableStates.find((s) => seededStateSet.has(s));
    setSelectedState(firstSeeded || availableStates[0]);
  }, [selectedState, availableStates, seededStateSet]);

  // ── Big-City cascade (per CEO Feb 2026 directive) ────────────────────
  // State → Big City → Region. Auto-selects the first big city with
  // regions inside the picked state so the panel never lands empty.
  const availableBigCities = useMemo(() => {
    const preferred = ['Dallas', 'Austin', 'Houston', 'San Antonio', 'Tyler'];
    const seen = new Set();
    (regions || []).forEach((r) => {
      if (selectedState && selectedState !== '__ALL__' && normState(r.state) !== selectedState) return;
      if (r.big_city) seen.add(r.big_city);
    });
    return [
      ...preferred.filter((c) => seen.has(c)),
      ...Array.from(seen).filter((c) => !preferred.includes(c)).sort(),
    ];
  }, [regions, selectedState]);
  // Reset big-city when state changes so cascade recomputes.
  useEffect(() => { setSelectedRegionId(null); }, [selectedState]);
  useEffect(() => { setSelectedRegionId(null); setSelectedCity(null); }, [selectedBigCity]);

  const usableRegions = useMemo(
    () => (regions || [])
      .filter((r) => (r.cities || []).length > 0)
      .filter((r) => !selectedState || selectedState === '__ALL__' || normState(r.state) === selectedState)
      .filter((r) => !selectedBigCity || selectedBigCity === '__ALL__' || (r.big_city || 'Unassigned') === selectedBigCity)
      .slice()
      .sort((a, b) => (a.region_code || 'zzz').localeCompare(b.region_code || 'zzz')),
    [regions, selectedState, selectedBigCity],
  );

  // ── Reports grouped by (region → city → agent) ──────────────────────
  const allEntries = useMemo(() => {
    // Unify incidents + safety-checks into one list of "reports"
    const norm = (obj, kind) => ({
      kind,
      key: `${kind}:${obj.incident_id || obj.report_id || Math.random()}`,
      created_at: obj.created_at || obj.timestamp,
      region_id: obj.region_id || null,
      region_code: obj.region_code || null,
      state: obj.state ? normState(obj.state) : 'TX',
      city: (obj.city || '').trim(),
      reporter_id: obj.reporter_id || obj.user_id || obj.reported_by || 'unknown',
      reporter_name: obj.reporter_name || obj.user_name || 'Unknown',
      reporter_role: obj.reporter_role || obj.role || null,
      reporter_position: obj.reporter_position || obj.position || null,
      raw: obj,
    });
    return [
      ...incidents.map((i) => norm(i, 'incident')),
      ...safetyReports.map((s) => norm(s, 'check')),
    ];
  }, [incidents, safetyReports]);

  // Region-scoped entries — match by region_id when set, else fall back
  // to region_code (some safety_incident docs stamp only region_code).
  const entriesForRegion = useMemo(() => {
    if (!selectedRegionId) return [];
    const selected = usableRegions.find((r) => r.region_id === selectedRegionId);
    const code = selected?.region_code;
    return allEntries.filter((e) =>
      e.region_id === selectedRegionId ||
      (e.region_id == null && code && e.region_code === code),
    );
  }, [allEntries, selectedRegionId, usableRegions]);

  // Cities within the selected region (union: region.cities catalog + any city seen in a report)
  const citiesForRegion = useMemo(() => {
    const selected = usableRegions.find((r) => r.region_id === selectedRegionId);
    const cities = new Set(selected?.cities || []);
    entriesForRegion.forEach((e) => { if (e.city) cities.add(e.city); });
    return Array.from(cities).sort();
  }, [usableRegions, selectedRegionId, entriesForRegion]);

  const entriesForCity = useMemo(() => {
    if (!selectedCity) return [];
    const key = selectedCity.trim().toLowerCase();
    return entriesForRegion.filter((e) => e.city.trim().toLowerCase() === key);
  }, [entriesForRegion, selectedCity]);

  // Agents in the selected city
  const agentsForCity = useMemo(() => {
    const map = new Map();
    entriesForCity.forEach((e) => {
      const key = e.reporter_id;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: e.reporter_name,
          role: e.reporter_role,
          position: e.reporter_position,
          count: 0,
          incidents: 0,
          checks: 0,
        });
      }
      const a = map.get(key);
      a.count += 1;
      if (e.kind === 'incident') a.incidents += 1;
      else a.checks += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [entriesForCity]);

  // Auto-select first region / city / agent as data changes
  useEffect(() => {
    if (selectedRegionId && usableRegions.some((r) => r.region_id === selectedRegionId)) return;
    if (usableRegions.length === 0) return;
    // Prefer a region that has entries (match by region_id OR region_code fallback)
    const withEntries = usableRegions.find((r) =>
      allEntries.some((e) =>
        e.region_id === r.region_id ||
        (e.region_id == null && e.region_code === r.region_code),
      ),
    );
    setSelectedRegionId((withEntries || usableRegions[0]).region_id);
  }, [usableRegions, allEntries, selectedRegionId]);

  useEffect(() => {
    if (selectedCity && citiesForRegion.includes(selectedCity)) return;
    if (citiesForRegion.length === 0) {
      setSelectedCity(null);
      return;
    }
    const withEntries = citiesForRegion.find((c) => {
      const key = c.trim().toLowerCase();
      return entriesForRegion.some((e) => e.city.trim().toLowerCase() === key);
    });
    setSelectedCity(withEntries || citiesForRegion[0]);
  }, [citiesForRegion, entriesForRegion, selectedCity]);

  useEffect(() => {
    if (agentsForCity.some((a) => a.id === selectedAgentId)) return;
    setSelectedAgentId(agentsForCity[0]?.id || null);
  }, [agentsForCity, selectedAgentId]);

  // ── Selected agent's report bundle ──────────────────────────────────
  const selectedAgent = useMemo(
    () => agentsForCity.find((a) => a.id === selectedAgentId) || null,
    [agentsForCity, selectedAgentId],
  );

  const agentEntries = useMemo(() => {
    if (!selectedAgentId) return [];
    return entriesForCity
      .filter((e) => e.reporter_id === selectedAgentId)
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  }, [entriesForCity, selectedAgentId]);

  const agentPhotoGallery = useMemo(() => {
    const urls = [];
    agentEntries.forEach((e) => {
      const raw = e.raw || {};
      (raw.photos || []).forEach((p) => {
        const src = typeof p === 'string' ? p : (p?.url || p?.data || p?.src);
        if (src) urls.push({ src, ctx: `${e.kind} · ${e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}` });
      });
      (raw.vehicle_photos || []).forEach((p) => {
        const src = typeof p === 'string' ? p : (p?.url || p?.data || p?.src);
        if (src) urls.push({ src, ctx: `vehicle photo · ${e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}` });
      });
    });
    return urls;
  }, [agentEntries]);

  // ── Actions ─────────────────────────────────────────────────────────
  const setIncidentStatus = async (incidentId, next) => {
    const res = await fetch(`${API}/api/safety-incidents/${incidentId}/status?status=${next}`, {
      method: 'PATCH', credentials: 'include',
    });
    if (res.ok) { toast.success('Status updated'); load(); }
    else toast.error('Update failed');
  };

  const routeToSafety = async () => {
    if (!selectedAgent) return;
    setRouting(true);
    try {
      const openIncidents = agentEntries.filter((e) => e.kind === 'incident' && (e.raw.status || 'open') === 'open');
      const summary = `Fleet safety escalation: ${selectedAgent.name}${selectedAgent.position ? ` (${selectedAgent.position})` : ''} — ${openIncidents.length} open incident(s), ${agentEntries.filter((e) => e.kind === 'check').length} safety check(s), ${agentPhotoGallery.length} photo(s).`;
      const res = await fetch(`${API}/api/dept-tasks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_department: 'safety',
          subject: `Safety review · ${selectedAgent.name}`,
          detail: summary,
          priority: openIncidents.length > 0 ? 'high' : 'normal',
          source_module: 'ops-fleet-safety-drilldown',
          reference_id: openIncidents[0]?.raw?.incident_id || null,
          related_entity: selectedAgent.name,
        }),
      });
      if (res.ok) toast.success('Routed to Safety & Risk Management');
      else toast.error('Routing failed — check backend');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRouting(false);
    }
  };

  const regionColorById = useMemo(() => (regions || []).reduce((acc, r) => {
    acc[r.region_id] = r.color || '#C9A962';
    return acc;
  }, {}), [regions]);

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <Card
      className="bg-zinc-900 border-2 border-red-500/40 p-4"
      data-testid="ops-fleet-safety-drilldown"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-red-500" />
          <h3
            className="text-lg font-bold text-white"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {(selectedBigCity || '').toUpperCase()} · FLEET SAFETY · REGION → CITY → AGENT
          </h3>
          <Badge className="bg-red-700 text-white">{allEntries.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" onClick={load} disabled={loading}
            className="h-8 border-zinc-700 text-zinc-300"
            data-testid="fleet-safety-refresh"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button
            onClick={routeToSafety}
            disabled={!selectedAgent || routing}
            className="h-8 bg-red-600 hover:bg-red-700 text-white text-[11px]"
            data-testid="fleet-safety-route-btn"
          >
            <ArrowRightLeft className="h-3 w-3 mr-1" />
            {routing ? 'Routing…' : 'Route to Safety & Risk'}
          </Button>
        </div>
      </div>

      {/* ── Big-City tab strip (matches Routes & Regions + every other
          Operations drill-down). Dallas · Houston · Austin · San
          Antonio · Tyler. State selector kept as small secondary. */}
      <div
        className="flex items-center justify-between gap-3 flex-wrap mb-3 pb-3 border-b border-zinc-800"
        data-testid="fleet-safety-bigcity-tabs"
      >
        <div className="flex items-center gap-1.5 flex-wrap">
          {['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'].map((city) => {
            const active = selectedBigCity === city;
            return (
              <button
                key={city}
                type="button"
                onClick={() => setSelectedBigCity(city)}
                className={`px-3.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all border ${
                  active
                    ? 'bg-[#FFD60A] text-black border-[#FFD60A] shadow-[0_0_0_1px_#FFD60A]'
                    : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-[#C9A962] hover:text-[#C9A962]'
                }`}
                data-testid={`fleet-safety-bigcity-tab-${city.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {city}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">State</span>
          <Select value={selectedState || '__ALL__'} onValueChange={setSelectedState}>
            <SelectTrigger
              className="h-7 w-[100px] bg-zinc-950 border-zinc-700 text-zinc-300 text-[11px]"
              data-testid="fleet-safety-state-select"
            >
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
              <SelectItem value="__ALL__">All</SelectItem>
              {availableStates.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                  {seededStateSet.has(s) && ' ·'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        {/* Column 1 · Regions */}
        <div className="xl:col-span-2">
          <ColumnHeader icon={MapPin} label="Regions" />
          <ul className="space-y-1 max-h-[500px] overflow-y-auto pr-1" data-testid="fleet-safety-region-list">
            {usableRegions.map((r) => {
              const active = r.region_id === selectedRegionId;
              // Count entries matching this region — either by region_id, or by region_code fallback.
              const count = allEntries.filter((e) =>
                e.region_id === r.region_id ||
                (e.region_id == null && e.region_code === r.region_code),
              ).length;
              return (
                <li key={r.region_id}>
                  <button
                    type="button"
                    onClick={() => { setSelectedRegionId(r.region_id); setSelectedCity(null); }}
                    className={`w-full text-left px-2 py-1.5 rounded flex items-center justify-between gap-2 text-[12px] ${
                      active ? 'bg-red-900/50 border border-red-500 text-white'
                             : 'bg-zinc-950 border border-zinc-800 text-zinc-300 hover:bg-zinc-900'
                    }`}
                    data-testid={`fleet-safety-region-${r.region_code}`}
                  >
                    <span className="truncate">
                      <span className="font-bold" style={{ color: regionColorById[r.region_id] }}>
                        {r.region_code}
                      </span>
                      {' '}· {r.state}
                    </span>
                    <Badge className={count ? 'bg-red-700 text-white' : 'bg-zinc-800 text-zinc-500'}>
                      {count}
                    </Badge>
                  </button>
                </li>
              );
            })}
            {usableRegions.length === 0 && (
              <li className="text-[11px] italic text-zinc-500 py-2">No regions in {selectedState || 'this state'}.</li>
            )}
          </ul>
        </div>

        {/* Column 2 · Cities */}
        <div className="xl:col-span-2">
          <ColumnHeader icon={Building2} label="Cities" />
          <ul className="space-y-1 max-h-[500px] overflow-y-auto pr-1" data-testid="fleet-safety-city-list">
            {citiesForRegion.map((c) => {
              const key = c.trim().toLowerCase();
              const count = entriesForRegion.filter((e) => e.city.trim().toLowerCase() === key).length;
              const active = c === selectedCity;
              return (
                <li key={c}>
                  <button
                    type="button"
                    onClick={() => setSelectedCity(c)}
                    className={`w-full text-left px-2 py-1.5 rounded flex items-center justify-between gap-2 text-[12px] ${
                      active ? 'bg-red-900/50 border border-red-500 text-white'
                             : 'bg-zinc-950 border border-zinc-800 text-zinc-300 hover:bg-zinc-900'
                    }`}
                    data-testid={`fleet-safety-city-${key}`}
                  >
                    <span className="truncate">{c}</span>
                    <Badge className={count ? 'bg-red-700 text-white' : 'bg-zinc-800 text-zinc-500'}>{count}</Badge>
                  </button>
                </li>
              );
            })}
            {citiesForRegion.length === 0 && (
              <li className="text-[11px] italic text-zinc-500 py-2">No cities in this region.</li>
            )}
          </ul>
        </div>

        {/* Column 3 · Agents (role + position) */}
        <div className="xl:col-span-3">
          <ColumnHeader icon={User} label="Agents (role · position)" />
          <ul className="space-y-1 max-h-[500px] overflow-y-auto pr-1" data-testid="fleet-safety-agent-list">
            {agentsForCity.map((a) => {
              const active = a.id === selectedAgentId;
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedAgentId(a.id)}
                    className={`w-full text-left px-2 py-1.5 rounded ${
                      active ? 'bg-red-900/50 border border-red-500 text-white'
                             : 'bg-zinc-950 border border-zinc-800 text-zinc-300 hover:bg-zinc-900'
                    }`}
                    data-testid={`fleet-safety-agent-${a.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-[12px] truncate">{a.name}</span>
                      <Badge className="bg-red-700 text-white text-[10px]">{a.count}</Badge>
                    </div>
                    <div className="text-[10px] text-zinc-400 truncate">
                      {[a.role, a.position].filter(Boolean).join(' · ') || 'Role/position unknown'}
                    </div>
                    <div className="flex gap-1 mt-1">
                      {a.incidents > 0 && (
                        <Badge className="bg-red-800 text-white text-[9px]">
                          <AlertTriangle className="h-2 w-2 mr-0.5" /> {a.incidents} incident{a.incidents === 1 ? '' : 's'}
                        </Badge>
                      )}
                      {a.checks > 0 && (
                        <Badge className="bg-emerald-800 text-white text-[9px]">
                          <ClipboardCheck className="h-2 w-2 mr-0.5" /> {a.checks} check{a.checks === 1 ? '' : 's'}
                        </Badge>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
            {agentsForCity.length === 0 && (
              <li className="text-[11px] italic text-zinc-500 py-2">No agents reported in this city.</li>
            )}
          </ul>
        </div>

        {/* Column 4 · Fleet safety detail */}
        <div className="xl:col-span-5">
          <ColumnHeader icon={Shield} label={selectedAgent ? `${selectedAgent.name} · Fleet Safety Record` : 'Fleet Safety Record'} />
          {!selectedAgent ? (
            <p className="text-xs italic text-zinc-500 py-4">
              Pick a region → city → agent to see their fleet-safety chain.
            </p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1" data-testid="fleet-safety-detail-panel">
              {/* Checklist section */}
              <SubHeader icon={ClipboardCheck} label={`Safety Checklists (${agentEntries.filter((e) => e.kind === 'check').length})`} />
              {agentEntries.filter((e) => e.kind === 'check').length === 0 && (
                <p className="text-[11px] italic text-zinc-500 pl-1">No pre-/post-shift checks on file.</p>
              )}
              {agentEntries.filter((e) => e.kind === 'check').map((e) => {
                const r = e.raw;
                return (
                  <Card key={e.key} className="bg-zinc-950 border-emerald-800/40 p-2.5" data-testid={`fleet-safety-check-${r.report_id}`}>
                    <div className="flex items-center gap-2 flex-wrap text-[11px]">
                      <Badge className="bg-emerald-700 text-white text-[10px]">{r.check_type || 'check'}</Badge>
                      <span className="text-zinc-300 font-mono">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </span>
                      {r.license_plate && (
                        <Badge className="bg-zinc-800 text-white text-[10px] font-mono">{r.license_plate}</Badge>
                      )}
                      {r.has_license === false && (
                        <Badge className="bg-red-700 text-white text-[10px]">No license photo</Badge>
                      )}
                    </div>
                    {r.checklist && typeof r.checklist === 'object' && (
                      <div className="text-[11px] text-zinc-400 mt-1 grid grid-cols-2 sm:grid-cols-3 gap-x-2">
                        {Object.entries(r.checklist).slice(0, 12).map(([k, v]) => (
                          <span key={k}>
                            <span className="text-zinc-500">{k.replace(/_/g, ' ')}:</span>{' '}
                            <span className={v ? 'text-emerald-400' : 'text-red-400'}>
                              {v === true ? '✓' : v === false ? '✗' : String(v)}
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </Card>
                );
              })}

              {/* Incident report section */}
              <SubHeader icon={AlertTriangle} label={`Incident Reports (${agentEntries.filter((e) => e.kind === 'incident').length})`} />
              {agentEntries.filter((e) => e.kind === 'incident').length === 0 && (
                <p className="text-[11px] italic text-zinc-500 pl-1">No incidents filed.</p>
              )}
              {agentEntries.filter((e) => e.kind === 'incident').map((e) => {
                const r = e.raw;
                return (
                  <Card
                    key={e.key}
                    className="bg-zinc-950 border-l-4 border-red-500 p-2.5"
                    data-testid={`fleet-safety-incident-${r.incident_id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap text-[11px]">
                      <Badge className={`${TYPE_TONE[r.incident_type] || 'bg-zinc-700'} text-white text-[10px]`}>
                        {(r.incident_type || 'other').replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-zinc-300 font-mono">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </span>
                      {r.license_plate && (
                        <Badge className="bg-zinc-800 text-white text-[10px] font-mono">{r.license_plate}</Badge>
                      )}
                      {r.police_case_number && (
                        <Badge className="bg-blue-700 text-white text-[10px]" title="Police case #">
                          Police #{r.police_case_number}
                        </Badge>
                      )}
                      {r.claim_number && (
                        <Badge className="bg-emerald-700 text-white text-[10px]">Claim #{r.claim_number}</Badge>
                      )}
                      {r.roadside_requested && (
                        <Badge className="bg-amber-600 text-white text-[10px] inline-flex items-center gap-1">
                          <Phone className="h-2.5 w-2.5" /> Roadside
                        </Badge>
                      )}
                    </div>
                    {r.description && (
                      <p className="text-[11px] text-zinc-300 mt-1">{r.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Select value={r.status || 'open'} onValueChange={(v) => setIncidentStatus(r.incident_id, v)}>
                        <SelectTrigger
                          className="h-7 w-[130px] bg-zinc-900 border-zinc-700 text-white text-[10px]"
                          data-testid={`fleet-safety-incident-status-${r.incident_id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_review">In review</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      {(r.photos?.length || 0) > 0 && (
                        <Badge className="bg-zinc-700 text-white text-[10px] inline-flex items-center gap-1">
                          <Camera className="h-2.5 w-2.5" /> {r.photos.length} photo{r.photos.length === 1 ? '' : 's'}
                        </Badge>
                      )}
                    </div>
                  </Card>
                );
              })}

              {/* Photo gallery — merged photos from every report */}
              <SubHeader icon={Camera} label={`Photo Gallery (${agentPhotoGallery.length})`} />
              {agentPhotoGallery.length === 0 ? (
                <p className="text-[11px] italic text-zinc-500 pl-1">No photos filed by this agent.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="fleet-safety-gallery">
                  {agentPhotoGallery.slice(0, 12).map((p, i) => (
                    <a
                      key={i}
                      href={p.src}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      data-testid={`fleet-safety-photo-${i}`}
                    >
                      <img
                        src={p.src}
                        alt={p.ctx}
                        className="w-full h-20 object-cover rounded border border-zinc-800 hover:border-red-500 transition-colors"
                      />
                      <p className="text-[9px] text-zinc-500 mt-0.5 truncate">{p.ctx}</p>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ── Small local subcomponents ─────────────────────────────────────── */
function ColumnHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 mb-2 pb-1 border-b border-zinc-800">
      <Icon className="h-3.5 w-3.5 text-[#C9A962]" />
      <span className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold">{label}</span>
    </div>
  );
}

function SubHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 mt-2">
      <Icon className="h-3.5 w-3.5 text-red-400" />
      <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold">{label}</span>
    </div>
  );
}
